import { Controller } from "@hotwired/stimulus"
import { visit } from "@hotwired/turbo"

export default class extends Controller {
  connect() {
    // Bind the method to preserve 'this' context
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    
    // Add event listener to the document for keyboard shortcuts
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    // Clean up event listener when controller is disconnected
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  handleKeydown(event) {
    // Only trigger if no modifier keys are pressed and we're not in an input/textarea
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return
    }

    // Check if we're currently focused on an input, textarea, or contenteditable element
    const activeElement = document.activeElement
    const isInputElement = activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.isContentEditable
    )

    // Don't trigger shortcuts if we're typing in an input field
    if (isInputElement) {
      return
    }

    // Handle left arrow (previous page)
    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.navigateToPreviousPage()
    }
    
    // Handle right arrow (next page)
    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.navigateToNextPage()
    }
  }

  navigateToPreviousPage() {
    const previousLink = this.findPaginationLink('previous')
    if (previousLink) {
      this.showNavigationFeedback('previous')
      
      // Use Turbo to navigate within the frame if it has data-turbo-frame
      if (previousLink.dataset.turboFrame) {
        visit(previousLink.href, { frame: previousLink.dataset.turboFrame })
      } else {
        visit(previousLink.href)
      }
    }
  }

  navigateToNextPage() {
    const nextLink = this.findPaginationLink('next')
    if (nextLink) {
      this.showNavigationFeedback('next')
      
      // Use Turbo to navigate within the frame if it has data-turbo-frame
      if (nextLink.dataset.turboFrame) {
        visit(nextLink.href, { frame: nextLink.dataset.turboFrame })
      } else {
        visit(nextLink.href)
      }
    }
  }

  findPaginationLink(direction) {
    // Look for pagination navigation container (pagy uses .pagination)
    const paginationContainer = document.querySelector('.pagination-nav, .pagination, nav[aria-label*="pagination"], nav[aria-label*="Pagination"]')
    if (!paginationContainer) {
      return null
    }

    if (direction === 'previous') {
      // Look for previous page link in various pagy formats
      // Check for pagy-specific classes and common aria labels
      const prevLink = paginationContainer.querySelector('a.prev, a[aria-label*="previous"], a[aria-label*="Previous"], a[title*="previous"], a[title*="Previous"]') ||
                      paginationContainer.querySelector('a[href*="page="]') ||
                      // Check for links with left arrow symbols
                      [...paginationContainer.querySelectorAll('a[href*="page="]')].find(link => 
                        /[‹←<]|prev|Previous/.test(link.textContent)
                      )
      
      // Make sure it's not disabled
      return prevLink && !prevLink.classList.contains('disabled') && !prevLink.getAttribute('aria-disabled') ? prevLink : null
    } else if (direction === 'next') {
      // Look for next page link in various pagy formats
      const nextLink = paginationContainer.querySelector('a.next, a[aria-label*="next"], a[aria-label*="Next"], a[title*="next"], a[title*="Next"]') ||
                      // Check for links with right arrow symbols
                      [...paginationContainer.querySelectorAll('a[href*="page="]')].find(link => 
                        /[›→>]|next|Next/.test(link.textContent)
                      )
      
      // Make sure it's not disabled  
      return nextLink && !nextLink.classList.contains('disabled') && !nextLink.getAttribute('aria-disabled') ? nextLink : null
    }

    return null
  }

  showNavigationFeedback(direction) {
    // Create a temporary visual indicator
    const indicator = document.createElement('div')
    indicator.className = 'fixed top-4 right-4 bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg z-50 transition-opacity'
    indicator.textContent = direction === 'previous' ? '← Previous Page' : 'Next Page →'
    
    document.body.appendChild(indicator)
    
    // Fade out and remove after a short delay
    setTimeout(() => {
      indicator.style.opacity = '0'
      setTimeout(() => {
        document.body.removeChild(indicator)
      }, 200)
    }, 1000)
  }
}